import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Calculator, Phone, TrendingUp, FileText, Receipt, CreditCard, Share2, Link as LinkIcon, Download } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { carModels, advisorInfo, loanDocuments } from '../data/mock';
import { toast } from 'sonner';

const LoanCalculator = () => {
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedVariant, setSelectedVariant] = useState('');
  const [customDeposit, setCustomDeposit] = useState('');
  const [tradeInValue, setTradeInValue] = useState(''); // Trade-in car value
  const [loanYears, setLoanYears] = useState('9');
  const [customRebateAmount, setCustomRebateAmount] = useState(''); // Custom rebate amount
  const [calculations, setCalculations] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareableLink, setShareableLink] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const calculateLoan = (otrPrice, deposit = 0, years = 9, interestRate = 2.90) => {
    const loanAmount = otrPrice - deposit;
    const interestTotal = loanAmount * (interestRate / 100) * years;
    const totalPayment = loanAmount + interestTotal;
    const monthly = Math.round(totalPayment / (years * 12));
    return {
      loanAmount,
      interestTotal,
      totalPayment,
      monthly
    };
  };

  const formatPrice = (price) => {
    return `RM${price.toLocaleString()}`;
  };

  const handleSaveAndShare = async () => {
    if (!calculations) {
      console.log('No calculations available');
      toast.error('Please select a model and variant first');
      return;
    }
    
    setIsSaving(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      console.log('Backend URL:', backendUrl);
      
      const payload = {
        model_name: calculations.model.name,
        variant_name: calculations.variant.name,
        otr_price: calculations.otrPrice,
        rebate_amount: calculations.rebateAmount || 0,
        effective_rebate: calculations.effectiveRebate || 0,
        price_after_rebate: calculations.priceAfterRebate,
        trade_in_value: calculations.tradeInValue || 0,
        custom_deposit: calculations.deposit || 0,
        total_deposit: calculations.totalDeposit || 0,
        loan_years: calculations.years,
        interest_rate: calculations.interestRate,
        monthly_payment: calculations.customDepositCalc?.monthly || calculations.tenPercentDeposit.monthly
      };
      
      console.log('Saving calculation:', payload);
      
      const response = await fetch(`${backendUrl}/api/calculator/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      console.log('Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Server error:', errorText);
        throw new Error(`Failed to save calculation: ${response.status}`);
      }

      const savedCalc = await response.json();
      console.log('Saved calculation:', savedCalc);
      
      const shareLink = `${window.location.origin}/calculator/shared/${savedCalc.id}`;
      setShareableLink(shareLink);
      setShowShareModal(true);
      toast.success('Calculation saved! Share link created.');
    } catch (error) {
      console.error('Error saving calculation:', error);
      toast.error(`Failed to save: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      // Try modern Clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(shareableLink);
        toast.success('Link copied to clipboard!');
      } else {
        // Fallback method for older browsers or non-secure contexts
        const textArea = document.createElement('textarea');
        textArea.value = shareableLink;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        try {
          const successful = document.execCommand('copy');
          if (successful) {
            toast.success('Link copied to clipboard!');
          } else {
            throw new Error('Copy command failed');
          }
        } finally {
          textArea.remove();
        }
      }
    } catch (err) {
      console.error('Failed to copy:', err);
      // If all else fails, select the text for manual copy
      const linkInput = document.getElementById('shareable-link-input');
      if (linkInput) {
        linkInput.select();
        toast.info('Please press Ctrl+C / Cmd+C to copy');
      } else {
        toast.error('Unable to copy. Please copy the link manually.');
      }
    }
  };

  const downloadQR = () => {
    const svg = document.getElementById('qr-code-svg');
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      const pngFile = canvas.toDataURL('image/png');
      
      const downloadLink = document.createElement('a');
      downloadLink.download = 'loan-calculator-qr.png';
      downloadLink.href = pngFile;
      downloadLink.click();
      toast.success('QR Code downloaded!');
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  const handleCalculate = () => {
    if (!selectedModel || !selectedVariant) return;

    const model = carModels.find(m => m.id === selectedModel);
    if (!model) return;
    
    const variant = model.variants.find(v => v.name === selectedVariant);
    if (!variant) return;
    
    const otrPrice = variant.price;
    const tradeIn = parseFloat(tradeInValue) || 0;
    const deposit = parseFloat(customDeposit) || 0;
    const totalDeposit = deposit + tradeIn; // Combine trade-in + custom deposit
    const years = parseInt(loanYears);
    const interestRate = model.interestRate;

    // Get rebate amount
    let rebateAmount = 0;
    let rebateInfo = null;
    
    if (model.rebate) {
      if (model.rebate.amount) {
        // Simple rebate amount
        rebateAmount = model.rebate.amount;
        rebateInfo = {
          amount: rebateAmount,
          condition: model.rebate.condition,
          type: 'simple'
        };
      } else if (model.rebate.variants && model.rebate.variants[selectedVariant]) {
        // Variant-specific rebate
        rebateAmount = model.rebate.variants[selectedVariant].amount;
        rebateInfo = {
          amount: rebateAmount,
          condition: model.rebate.variants[selectedVariant].condition,
          type: 'variant',
          bonus: model.rebate.variants[selectedVariant].bonus
        };
      }
    }

    // Use custom rebate amount if provided, otherwise use original rebate
    const effectiveRebate = customRebateAmount !== '' ? parseFloat(customRebateAmount) || 0 : rebateAmount;
    const priceAfterRebate = otrPrice - effectiveRebate;

    // Calculate different scenarios WITH or WITHOUT REBATE based on toggle
    const fullLoan = calculateLoan(priceAfterRebate, 0, years, interestRate);
    const tenPercentDeposit = calculateLoan(priceAfterRebate, priceAfterRebate * 0.1, years, interestRate);
    const customDepositCalc = totalDeposit > 0 ? calculateLoan(priceAfterRebate, totalDeposit, years, interestRate) : null;

    setCalculations({
      otrPrice,
      rebateAmount,
      rebateInfo,
      effectiveRebate,
      priceAfterRebate,
      deposit,
      tradeInValue: tradeIn,
      totalDeposit,
      years,
      interestRate,
      fullLoan,
      tenPercentDeposit,
      customDepositCalc,
      model,
      variant
    });
  };

  const getModelOptions = () => {
    return carModels.map(model => (
      <SelectItem key={model.id} value={model.id}>
        {model.name}
      </SelectItem>
    ));
  };

  const getVariantOptions = () => {
    if (!selectedModel) return [];
    const model = carModels.find(m => m.id === selectedModel);
    return model.variants.map(variant => (
      <SelectItem key={variant.name} value={variant.name}>
        {variant.name} - {formatPrice(variant.price)}
      </SelectItem>
    ));
  };

  // Auto-fill custom rebate when model changes
  useEffect(() => {
    if (selectedModel && selectedVariant) {
      const model = carModels.find(m => m.id === selectedModel);
      if (model && model.rebate) {
        // Auto-default to original rebate amount
        let defaultRebate = 0;
        if (model.rebate.amount) {
          defaultRebate = model.rebate.amount;
        } else if (model.rebate.variants && model.rebate.variants[selectedVariant]) {
          defaultRebate = model.rebate.variants[selectedVariant].amount;
        }
        setCustomRebateAmount(defaultRebate.toString());
      } else {
        setCustomRebateAmount('0');
      }
    }
  }, [selectedModel, selectedVariant]);

  useEffect(() => {
    if (selectedModel && selectedVariant) {
      handleCalculate();
    }
  }, [selectedModel, selectedVariant, customDeposit, tradeInValue, loanYears, customRebateAmount]);

  return (
    <section id="calculator" className="py-20 bg-gradient-to-br from-pink-50 to-purple-100">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-pink-100 text-pink-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Calculator className="w-4 h-4" />
            <span>Loan Calculator</span>
          </div>
          <h2 className="text-4xl font-bold text-slate-900 mb-4">
            Kira Ansuran 
            <span className="text-pink-600"> Bulanan Anda</span>
          </h2>
          <p className="text-xl text-slate-600">
            Dapatkan anggaran tepat dengan custom deposit dan tempoh pinjaman
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 md:gap-8">
          {/* Calculator Form */}
          <Card className="bg-white shadow-xl rounded-2xl border-0">
            <CardHeader>
              <CardTitle className="text-xl font-bold text-slate-900 flex items-center space-x-2">
                <Calculator className="w-6 h-6 text-pink-600" />
                <span>Loan Calculator</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Model Selection */}
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-2 block">
                  Pilih Model Kereta
                </Label>
                <Select value={selectedModel} onValueChange={setSelectedModel}>
                  <SelectTrigger>
                    <SelectValue placeholder="Pilih model Perodua" />
                  </SelectTrigger>
                  <SelectContent>
                    {getModelOptions()}
                  </SelectContent>
                </Select>
              </div>

              {/* Variant Selection */}
              {selectedModel && (
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-2 block">
                    Pilih Variant
                  </Label>
                  <Select value={selectedVariant} onValueChange={setSelectedVariant}>
                    <SelectTrigger>
                      <SelectValue placeholder="Pilih variant" />
                    </SelectTrigger>
                    <SelectContent>
                      {getVariantOptions()}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Trade-In Estimator */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border-2 border-blue-200">
                <Label className="text-sm font-bold text-blue-900 mb-2 block flex items-center">
                  <span className="mr-2">🚗</span>
                  Trade-In Old Car (Optional)
                </Label>
                <Input
                  type="number"
                  placeholder="Nilai kereta lama (RM)"
                  value={tradeInValue}
                  onChange={(e) => setTradeInValue(e.target.value)}
                  className="w-full border-blue-300 focus:border-blue-500"
                />
                <p className="text-xs text-blue-600 mt-2 bg-blue-100 rounded px-2 py-1">
                  💡 Kereta lama boleh jadi deposit! Nilai trade-in akan auto-tambah dalam deposit.
                </p>
              </div>

              {/* Custom Deposit */}
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-2 block">
                  Additional Deposit (Optional)
                </Label>
                <Input
                  type="number"
                  placeholder="Deposit tambahan (RM)"
                  value={customDeposit}
                  onChange={(e) => setCustomDeposit(e.target.value)}
                  className="w-full"
                />
              </div>

              {/* Loan Years */}
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-2 block">
                  Tempoh Pinjaman
                </Label>
                <Select value={loanYears} onValueChange={setLoanYears}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5 Tahun</SelectItem>
                    <SelectItem value="6">6 Tahun</SelectItem>
                    <SelectItem value="7">7 Tahun</SelectItem>
                    <SelectItem value="8">8 Tahun</SelectItem>
                    <SelectItem value="9">9 Tahun (Default)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Custom Rebate Amount - Only show if model has rebate */}
              {selectedModel && carModels.find(m => m.id === selectedModel)?.rebate && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700 flex items-center">
                    <span className="mr-2">🎁</span>
                    Custom Rebate Amount
                  </Label>
                  <div className="relative">
                    <Input
                      type="number"
                      id="customRebate"
                      placeholder="0"
                      value={customRebateAmount}
                      onChange={(e) => setCustomRebateAmount(e.target.value)}
                      className="w-full pl-12 text-lg font-semibold border-2 border-orange-300 focus:border-orange-500"
                    />
                    <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-600 font-semibold">
                      RM
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 bg-orange-50 border border-orange-200 rounded px-3 py-2">
                    💡 <strong>Default:</strong> Auto-fill dengan rebate asal. Boleh increase untuk special customer atau set ke 0 untuk tiada rebate.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Results */}
          {calculations && (
            <Card className="bg-white shadow-xl rounded-2xl border-0">
              <CardHeader>
                <CardTitle className="text-xl font-bold text-slate-900 flex items-center space-x-2">
                  <TrendingUp className="w-6 h-6 text-green-600" />
                  <span>Hasil Kiraan</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 md:space-y-6">
                {/* Car Image & Model Info - COMPACT */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-2 md:p-4">
                  {/* Car Image */}
                  <div className="bg-white rounded-lg p-2 mb-2 flex items-center justify-center">
                    <img 
                      src={calculations.model.image} 
                      alt={calculations.model.name}
                      className="w-full max-w-[140px] md:max-w-[200px] h-auto object-contain"
                    />
                  </div>
                  
                  <h4 className="font-bold text-blue-900 mb-1 text-xs md:text-base text-center">
                    {calculations.model.name}
                  </h4>
                  <div className="text-center bg-blue-100 rounded-full px-2 py-0.5 w-full">
                    <span className="text-[10px] md:text-sm font-semibold text-blue-800">
                      {calculations.variant.name}
                    </span>
                  </div>
                  
                  <div className="space-y-1 mt-2">
                    <div className="flex justify-between items-center bg-white rounded px-2 py-1.5">
                      <span className="text-[9px] md:text-sm text-blue-700 font-medium">Harga OTR:</span>
                      <span className="text-xs md:text-lg font-bold text-blue-900">
                        {formatPrice(calculations.otrPrice)}
                      </span>
                    </div>
                    
                    {/* Rebate Applied - Only show if effectiveRebate > 0 */}
                    {calculations.effectiveRebate > 0 && (
                      <>
                        <div className="flex justify-between items-center py-1.5 px-2 bg-gradient-to-r from-orange-100 to-red-100 rounded-lg">
                          <span className="text-[9px] md:text-sm font-bold text-orange-700 flex items-center">
                            <span className="mr-1">🎁</span>
                            Rebate {calculations.effectiveRebate !== calculations.rebateAmount && '(Custom)'}:
                          </span>
                          <span className="text-xs md:text-lg font-bold text-red-600">
                            - {formatPrice(calculations.effectiveRebate)}
                          </span>
                        </div>
                        {calculations.effectiveRebate !== calculations.rebateAmount && calculations.rebateAmount > 0 && (
                          <div className="text-[8px] md:text-xs text-orange-600 bg-orange-50 rounded px-2 py-1 text-center">
                            {calculations.effectiveRebate > calculations.rebateAmount 
                              ? `✨ Extra rebate! Original: ${formatPrice(calculations.rebateAmount)}`
                              : `Original rebate: ${formatPrice(calculations.rebateAmount)}`
                            }
                          </div>
                        )}
                      </>
                    )}
                    
                    {/* Harga Final - Always show */}
                    <div className="flex justify-between items-center bg-green-50 rounded px-2 py-1.5 border-2 border-green-300">
                      <span className="text-[9px] md:text-sm font-bold text-green-700">Harga Final:</span>
                      <span className="text-sm md:text-xl font-bold text-green-600">
                        {formatPrice(calculations.priceAfterRebate)}
                      </span>
                    </div>
                    
                    <div className="flex justify-between items-center bg-white rounded px-2 py-1.5">
                      <span className="text-[9px] md:text-sm text-blue-700 font-medium">Interest:</span>
                      <span className="text-[9px] md:text-sm font-bold text-blue-900">
                        {calculations.model.interestRateRange || `${calculations.interestRate}%`}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Loan Options - COMPACT & PRIORITIZED */}
                <div className="space-y-2">
                  {/* Only show rebate status if rebate is actually applied */}
                  {calculations.effectiveRebate > 0 && (
                    <div className="bg-green-50 border-green-200 border rounded-lg p-2 text-center">
                      <p className="text-[9px] md:text-sm font-semibold text-green-700">
                        {calculations.rebateAmount > 0 && calculations.effectiveRebate === calculations.rebateAmount 
                          ? `✨ Rebate digunakan: RM${calculations.effectiveRebate.toLocaleString()}`
                          : calculations.rebateAmount > 0 && calculations.effectiveRebate > calculations.rebateAmount
                            ? `🎉 Extra Rebate! RM${calculations.effectiveRebate.toLocaleString()} (Original: RM${calculations.rebateAmount.toLocaleString()})`
                            : `✨ Custom Rebate: RM${calculations.effectiveRebate.toLocaleString()}`
                        }
                      </p>
                    </div>
                  )}
                  
                  <h3 className="text-xs font-bold text-slate-700 mb-1 flex items-center">
                    <span className="mr-1">💳</span>
                    PILIHAN ANSURAN{calculations.effectiveRebate > 0 ? ' (Dengan Rebate)' : ''}
                  </h3>
                  
                  {/* PRIORITY 1: Custom Deposit (if exists) - SHOW FIRST */}
                  {calculations.customDepositCalc && (
                    <Card className="bg-gradient-to-br from-orange-500 to-red-600 text-white border-0 shadow-lg">
                      <CardContent className="p-2.5">
                        <div className="flex justify-between items-center mb-1">
                          <div className="text-[9px] font-medium">
                            {calculations.tradeInValue > 0 ? 'With Trade-In' : 'Custom Deposit'} ({calculations.years} Tahun)
                          </div>
                          <Badge className="bg-yellow-400 text-orange-900 font-bold text-[8px] px-1.5 py-0">YOUR CHOICE</Badge>
                        </div>
                        <div className="text-2xl font-bold mb-1">
                          {formatPrice(calculations.customDepositCalc.monthly)}<span className="text-xs">/bln</span>
                        </div>
                        <div className="grid grid-cols-2 gap-1 text-[9px] text-orange-100 mb-1">
                          {calculations.tradeInValue > 0 && (
                            <>
                              <div>🚗 Trade-In: {formatPrice(calculations.tradeInValue)}</div>
                              {calculations.deposit > 0 && (
                                <div className="text-right">💵 + Deposit: {formatPrice(calculations.deposit)}</div>
                              )}
                            </>
                          )}
                          {!calculations.tradeInValue && (
                            <div>💵 Deposit: {formatPrice(calculations.deposit)}</div>
                          )}
                          <div className={calculations.tradeInValue > 0 ? "col-span-2 text-center" : "text-right"}>
                            🏦 Loan: {formatPrice(calculations.priceAfterRebate - calculations.totalDeposit)}
                          </div>
                        </div>
                        <div className="text-[8px] text-center bg-white/20 rounded px-1 py-0.5">
                          💰 Jimat RM{(calculations.fullLoan.monthly - calculations.customDepositCalc.monthly).toLocaleString()}/bulan vs Full Loan
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* PRIORITY 2: 10% Deposit */}
                  <Card className="bg-gradient-to-br from-purple-500 to-pink-600 text-white border-0 shadow-md">
                    <CardContent className="p-2.5">
                      <div className="text-[9px] font-medium mb-1">10% Deposit ({calculations.years} Tahun)</div>
                      <div className="text-2xl font-bold mb-1">
                        {formatPrice(calculations.tenPercentDeposit.monthly)}<span className="text-xs">/bln</span>
                      </div>
                      <div className="grid grid-cols-2 gap-1 text-[9px] text-purple-100 mb-1">
                        <div>💵 Deposit: {formatPrice(calculations.priceAfterRebate * 0.1)}</div>
                        <div className="text-right">🏦 Loan: {formatPrice(calculations.priceAfterRebate * 0.9)}</div>
                      </div>
                      <div className="text-[8px] text-center bg-white/20 rounded px-1 py-0.5">
                        💰 Jimat RM{(calculations.fullLoan.monthly - calculations.tenPercentDeposit.monthly).toLocaleString()}/bulan vs Full Loan
                      </div>
                    </CardContent>
                  </Card>

                  {/* PRIORITY 3: Full Loan */}
                  {calculations.years === 9 && (
                    <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white border-0 shadow-md">
                      <CardContent className="p-2.5">
                        <div className="flex justify-between items-center mb-1">
                          <div className="text-[9px] font-medium">9 Tahun - Full Loan</div>
                          <Badge className="bg-yellow-400 text-green-900 font-bold text-[8px] px-1.5 py-0">LOWEST</Badge>
                        </div>
                        <div className="text-2xl font-bold mb-1">
                          {formatPrice(calculations.fullLoan.monthly)}<span className="text-xs">/bln</span>
                        </div>
                        <div className="grid grid-cols-2 gap-1 text-[9px] text-green-100">
                          <div>💵 Deposit: RM0</div>
                          <div className="text-right">🏦 Loan: {formatPrice(calculations.priceAfterRebate)}</div>
                        </div>
                        <div className="text-[8px] text-center bg-white/20 rounded px-1 py-0.5 mt-1">
                          Ansuran terendah • Interest: {calculations.interestRate}%
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {calculations.years === 7 && (
                    <Card className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white border-0 shadow-md">
                      <CardContent className="p-2.5">
                        <div className="flex justify-between items-center mb-1">
                          <div className="text-[9px] font-medium">7 Tahun - Full Loan</div>
                          <Badge className="bg-yellow-400 text-blue-900 font-bold text-[8px] px-1.5 py-0">FAST</Badge>
                        </div>
                        <div className="text-2xl font-bold mb-1">
                          {formatPrice(calculations.fullLoan.monthly)}<span className="text-xs">/bln</span>
                        </div>
                        <div className="grid grid-cols-2 gap-1 text-[9px] text-blue-100">
                          <div>💵 Deposit: RM0</div>
                          <div className="text-right">🏦 Loan: {formatPrice(calculations.priceAfterRebate)}</div>
                        </div>
                        <div className="text-[8px] text-center bg-white/20 rounded px-1 py-0.5 mt-1">
                          Habis cepat • Interest: {calculations.interestRate}%
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="grid grid-cols-2 gap-2">
                  {/* WhatsApp CTA */}
                  <Button 
                    onClick={() => {
                      let message = `[Website] PM PERODUA - Hi Syasyah, saya berminat ${calculations.model.name} ${calculations.variant.name}.\n\n`;
                      message += `💰 Ansuran: ${formatPrice(calculations.customDepositCalc?.monthly || calculations.tenPercentDeposit.monthly)}/bulan\n`;
                      message += `⏱️ Tempoh: ${calculations.years} tahun\n`;
                      if (calculations.tradeInValue > 0) {
                        message += `🚗 Trade-in kereta lama: ${formatPrice(calculations.tradeInValue)}\n`;
                      }
                      if (calculations.deposit > 0) {
                        message += `💵 Deposit tambahan: ${formatPrice(calculations.deposit)}\n`;
                      }
                      if (calculations.effectiveRebate > 0) {
                        message += `🎁 Rebate: ${formatPrice(calculations.effectiveRebate)}\n`;
                      }
                      message += `\nBoleh proceed?`;
                      
                      window.open(`${advisorInfo.whatsappLink}?text=${encodeURIComponent(message)}`, '_blank');
                    }}
                    className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2.5 rounded-lg transition-colors text-xs md:text-sm"
                  >
                    <Phone className="w-3 h-3 mr-1 md:mr-2" />
                    WhatsApp
                  </Button>

                  {/* Save & Share Button */}
                  <Button 
                    onClick={handleSaveAndShare}
                    disabled={isSaving}
                    className="bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2.5 rounded-lg transition-colors text-xs md:text-sm"
                  >
                    <Share2 className="w-3 h-3 mr-1 md:mr-2" />
                    {isSaving ? 'Saving...' : 'Save & Share'}
                  </Button>
                </div>

                {/* Compact Info */}
                <div className="text-[8px] md:text-xs text-slate-500 text-center bg-slate-50 rounded-lg p-1.5">
                  *Anggaran • {calculations.model.waitingList}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Document Requirements Section */}
        <div className="mt-16 bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">
              📋 Dokumen Diperlukan Untuk Loan
            </h3>
            <p className="text-slate-600">
              Sediakan dokumen berikut untuk memudahkan proses permohonan loan
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center p-6 bg-blue-50 rounded-xl border border-blue-200">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-blue-600" />
              </div>
              <h4 className="font-semibold text-slate-900 mb-2">Copy IC/Lesen</h4>
              <p className="text-sm text-slate-600">
                Salinan kad pengenalan dan lesen memandu (depan & belakang)
              </p>
            </div>

            <div className="text-center p-6 bg-green-50 rounded-xl border border-green-200">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Receipt className="w-8 h-8 text-green-600" />
              </div>
              <h4 className="font-semibold text-slate-900 mb-2">Payslip 3 Bulan</h4>
              <p className="text-sm text-slate-600">
                Slip gaji 3 bulan terkini dari majikan
              </p>
            </div>

            <div className="text-center p-6 bg-purple-50 rounded-xl border border-purple-200">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CreditCard className="w-8 h-8 text-purple-600" />
              </div>
              <h4 className="font-semibold text-slate-900 mb-2">Statement Bank 3 Bulan</h4>
              <p className="text-sm text-slate-600">
                Penyata bank 3 bulan terkini
              </p>
            </div>
          </div>

          <div className="mt-8 text-center">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-yellow-800">
                💡 <strong>Tips:</strong> Sediakan dokumen lengkap untuk mempercepatkan proses kelulusan loan
              </p>
            </div>
            
            <Button 
              onClick={() => window.open(`${advisorInfo.whatsappLink}?text=${encodeURIComponent('[Website] PM PERODUA - Hi Syasyah, saya nak tanya tentang dokumen yang diperlukan untuk loan application')}`, '_blank')}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-3 rounded-lg transition-colors"
            >
              <Phone className="w-5 h-5 mr-2" />
              Tanya Syasyah Tentang Dokumen
            </Button>
          </div>
        </div>
      </div>

      {/* Share Modal */}
      <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center text-xl font-bold">Share Your Calculation</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* QR Code */}
            <div className="flex justify-center bg-white p-4 rounded-lg">
              <QRCodeSVG 
                id="qr-code-svg"
                value={shareableLink} 
                size={200}
                level="H"
                includeMargin={true}
              />
            </div>

            {/* Link Display */}
            <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
              <p className="text-xs text-slate-600 mb-2 font-medium">Shareable Link:</p>
              <div className="flex items-center space-x-2">
                <input 
                  id="shareable-link-input"
                  type="text" 
                  value={shareableLink} 
                  readOnly 
                  onClick={(e) => e.target.select()}
                  className="flex-1 bg-white border border-slate-300 rounded px-3 py-2 text-sm cursor-pointer"
                />
                <Button 
                  onClick={copyToClipboard}
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  title="Copy Link"
                >
                  <LinkIcon className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                💡 Click link to select, then copy manually or click button
              </p>
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-2 gap-3">
              <Button 
                onClick={downloadQR}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                <Download className="w-4 h-4 mr-2" />
                Download QR
              </Button>
              <Button 
                onClick={() => {
                  const message = `Hi! Saya share loan calculation untuk Perodua. Check it out: ${shareableLink}`;
                  window.open(`${advisorInfo.whatsappLink}?text=${encodeURIComponent(message)}`, '_blank');
                }}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <Phone className="w-4 h-4 mr-2" />
                Share via WA
              </Button>
            </div>

            {/* Info */}
            <p className="text-xs text-center text-slate-500">
              💡 Link valid for 30 days. Scan QR or share link dengan family/friends!
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default LoanCalculator;